import { Camera, Color4, FreeCamera, Mesh, Quaternion, RenderTargetTexture, Scene, Vector3 } from 'babylonjs';
import type { PlaneBasis } from '../../solver/types';
import type { RectState } from '../../core/types';

export interface CaptureOptions {
  samples?: number; // MSAA samples (default 1 — multisampled RTT readback is fragile)
  slabHalfDepth?: number; // half-thickness of the captured depth slab around the plane
  onProgress?: (done: number, total: number) => void;
}

export interface CaptureResultRaw {
  blob: Blob;
  width: number;
  height: number;
  worldWidth: number;
  worldHeight: number;
}

const v3 = (t: readonly [number, number, number]) => new Vector3(t[0], t[1], t[2]);

/**
 * Render a flat region front-on at high resolution by rendering an orthographic
 * camera (model meshes only, transparent clear) into a RenderTargetTexture, then
 * reading it back. The RTT is rendered through the SCENE pipeline (pushed onto
 * scene.customRenderTargets + scene.render()) — NOT via rtt.render() directly and
 * NOT via CreateScreenshotUsingRenderTarget, both of which read back blank on some
 * GPUs. This neither resizes nor reads the live canvas, so the viewport is
 * untouched. readPixels is bottom-up, so rows are flipped into the PNG.
 *
 * Callers should suppress FX and swap materials to unlit BEFORE calling.
 */
export async function captureRegion(
  scene: Scene,
  modelMeshes: Mesh[],
  basis: PlaneBasis,
  rect: RectState,
  width: number,
  height: number,
  opts: CaptureOptions = {},
): Promise<CaptureResultRaw> {
  const engine = scene.getEngine();
  const caps = engine.getCaps();
  const maxDim = Math.min(caps.maxTextureSize, caps.maxRenderTextureSize ?? caps.maxTextureSize, 8192);

  // Clamp to a single RTT, preserving aspect.
  let outW = Math.max(1, Math.round(width));
  let outH = Math.max(1, Math.round(height));
  const longest = Math.max(outW, outH);
  if (longest > maxDim) {
    const k = maxDim / longest;
    outW = Math.max(1, Math.round(outW * k));
    outH = Math.max(1, Math.round(outH * k));
  }

  const worldWidth = rect.halfW * 2;
  const worldHeight = rect.halfH * 2;
  const slabHalfDepth = opts.slabHalfDepth ?? Math.max(0.15 * Math.max(worldWidth, worldHeight), 0.05);
  const standoff = Math.max(worldWidth, worldHeight) + slabHalfDepth * 2;

  const normal = v3(basis.normal);
  const center = v3(basis.origin)
    .add(v3(basis.right).scale(rect.center[0]))
    .add(v3(basis.up).scale(rect.center[1]));
  const up = Vector3.Zero();
  v3(basis.up).rotateByQuaternionToRef(Quaternion.RotationAxis(normal, rect.angle), up);

  // Orthographic, front-on; the depth slab clips foreground/background clutter.
  const cam = new FreeCamera('pfCaptureCam', center.add(normal.scale(standoff)), scene);
  cam.mode = Camera.ORTHOGRAPHIC_CAMERA;
  cam.minZ = standoff - slabHalfDepth;
  cam.maxZ = standoff + slabHalfDepth;
  cam.upVector = up;
  cam.setTarget(center);
  cam.orthoLeft = -rect.halfW;
  cam.orthoRight = rect.halfW;
  cam.orthoTop = rect.halfH;
  cam.orthoBottom = -rect.halfH;

  const rtt = new RenderTargetTexture('pfCapture', { width: outW, height: outH }, scene, {
    generateDepthBuffer: true,
    generateMipMaps: false,
    samples: opts.samples ?? 1,
  });
  rtt.clearColor = new Color4(0, 0, 0, 0); // off-region stays transparent
  rtt.renderList = modelMeshes;
  rtt.activeCamera = cam;

  // The capture camera is NOT the scene's active camera, so its matrices are
  // never computed by the render loop — force them, or the RTT renders blank.
  cam.getViewMatrix();
  cam.getProjectionMatrix(true);

  let raw: Uint8Array | null = null;
  scene.customRenderTargets.push(rtt);
  try {
    // The caller just swapped the materials to unlit, so this is the first use of the
    // capture permutation (unlit albedo + the FX cull plugin). Wait for it to compile
    // before reading back, so a not-yet-linked program can't yield a blank frame.
    // Bounded by a wall-clock deadline (not rAF / iteration count) so a throttled or
    // backgrounded tab can never stall the capture.
    scene.render();
    const readyBy = Date.now() + 4000;
    while (Date.now() < readyBy && !modelMeshes.every((m) => m.isReady(true))) {
      await new Promise((r) => setTimeout(r, 16));
      scene.render();
    }

    // Draw the RTT and read it back. Different GPU backends render a custom render
    // target through different code paths, and one or the other reads back BLANK
    // (all-zero) depending on the backend:
    //   • the scene pass (RTT on scene.customRenderTargets, drawn by scene.render())
    //     works on most desktop GL drivers;
    //   • an explicit rtt.render() works on ANGLE's Metal backend (Apple Silicon),
    //     where the customRenderTargets pass reads back all-zero.
    // Neither is universal, so alternate BOTH paths until one yields pixels.
    //
    // The retry loop (not a single-shot fallback) matters: Babylon 7 compiles effects
    // PER RENDER PASS, and the RTT has its own render pass id — so even after the
    // isReady() wait above (which vouches only for the main pass), the RTT-pass
    // effect can still be linking asynchronously (KHR_parallel_shader_compile) on the
    // very first capture after a load. A one-shot fallback then reads back blank; the
    // next capture finds the now-compiled effect cached and works — i.e. "first
    // capture transparent, second fine". Looping until non-blank (or deadline) gives
    // the async link the time it needs on ALL backends.
    scene.render();
    raw = (await rtt.readPixels()) as Uint8Array | null;
    const blankBy = Date.now() + 6000;
    while (isBlankReadback(raw) && Date.now() < blankBy) {
      rtt.render(false); // render the RTT directly (no camera post-process)
      const alt = (await rtt.readPixels()) as Uint8Array | null;
      if (!isBlankReadback(alt)) {
        raw = alt;
        break;
      }
      await new Promise((r) => setTimeout(r, 32));
      scene.render(); // scene-pass attempt again (also ticks pending compiles)
      raw = (await rtt.readPixels()) as Uint8Array | null;
    }
    // Still blank at the deadline → FAIL, don't silently download a fully
    // transparent PNG (the user would just get a mysteriously empty file).
    if (isBlankReadback(raw)) {
      throw new Error('capture render target read back blank (GPU readback unsupported?)');
    }
  } finally {
    const idx = scene.customRenderTargets.indexOf(rtt);
    if (idx >= 0) scene.customRenderTargets.splice(idx, 1);
    rtt.dispose();
    cam.dispose();
  }
  opts.onProgress?.(1, 1);

  const stitch = createCanvas(outW, outH);
  const ctx = stitch.getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error('plane-finder: 2D canvas context unavailable');
  if (raw) {
    const img = ctx.createImageData(outW, outH);
    for (let y = 0; y < outH; y++) {
      const src = (outH - 1 - y) * outW * 4; // GL readback is bottom-up
      img.data.set(raw.subarray(src, src + outW * 4), y * outW * 4);
    }
    ctx.putImageData(img as ImageData, 0, 0);
  }

  const blob = await canvasToBlob(stitch);
  return { blob, width: outW, height: outH, worldWidth, worldHeight };
}

/** True if an RGBA readback is fully transparent (every alpha byte 0) — i.e. the
 *  render target came back blank because this GPU backend didn't draw into it. */
function isBlankReadback(buf: Uint8Array | null): boolean {
  if (!buf) return true;
  for (let i = 3; i < buf.length; i += 4) if (buf[i] !== 0) return false;
  return true;
}

function createCanvas(w: number, h: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

async function canvasToBlob(canvas: OffscreenCanvas | HTMLCanvasElement): Promise<Blob> {
  if ('convertToBlob' in canvas) return canvas.convertToBlob({ type: 'image/png' });
  return new Promise<Blob>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
}
